import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { memberAPI } from '../api/memberAPI';
import { userAPI } from '../api/userAPI';
import { serviceAPI } from '../api/serviceAPI';
import { Avatar } from '../components/Avatar';
import { AbsenceReasonPopover } from '../components/AbsenceReasonPopover';

function formatServiceDate(dateStr, type) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayName = type === 'sunday' ? 'Sunday' : 'Wednesday';
    return `${dayName}, ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

function formatShortDate(dateStr, type) {
    const [, month, day] = dateStr.split('-').map(Number);
    const prefix = type === 'sunday' ? 'Sun' : 'Wed';
    return `${prefix} ${month}/${day}`;
}

export function Attendance() {
    const { isAuthenticated, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [presentSet, setPresentSet] = useState(new Set());
    const [recentServices, setRecentServices] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reasonMap, setReasonMap] = useState({});
    const [openPopoverId, setOpenPopoverId] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [syncError, setSyncError] = useState(false);
    const esRef = useRef(null);
    const serviceIdRef = useRef(null);
    const [attendanceMode, setAttendanceMode] = useState(isAdmin ? 'leaders' : 'members');
    const [leaders, setLeaders] = useState([]);
    const membersRef = useRef(members);
    membersRef.current = members;
    const leadersRef = useRef(leaders);
    leadersRef.current = leaders;

    const selectedService = recentServices[selectedIndex] || null;
    const isCurrentService = selectedService?.is_current ?? false;

    // Load members on mount
    useEffect(() => {
        if (!isAuthenticated) return;
        memberAPI.getMembers({ per_page: 1000, status: 'active' })
            .then((res) => {
                const items = (res.data.data || {}).items || [];
                setMembers(items);
            })
            .catch(() => setError('Failed to load members'))
            .finally(() => setLoading(false));
    }, [isAuthenticated]);

    // Load leaders on mount (admin only)
    useEffect(() => {
        if (!isAuthenticated || !isAdmin) return;
        userAPI.getUsers({ per_page: 1000 })
            .then((res) => {
                const items = (res.data.data || {}).items || [];
                setLeaders(items.filter((l) => l.status === 'active' && l.roles?.some((r) => ['regional_leader', 'regional_assistant'].includes(r))));
            })
            .catch(() => {});
    }, [isAuthenticated, isAdmin]);

    // Apply absences from a service response to presentSet and reasonMap
    const applyServiceData = useCallback((serviceData) => {
        const absences = serviceData.absences || [];
        const absentIds = new Set(absences.map((a) => a.member_id));
        const allPeople = [...membersRef.current, ...leadersRef.current];
        setPresentSet(new Set(allPeople.filter((p) => !absentIds.has(p.id)).map((p) => p.id)));
        const reasons = {};
        absences.forEach((a) => { if (a.reason) reasons[a.member_id] = a.reason; });
        setReasonMap(reasons);
    }, []);

    // Load recent services
    const loadRecentServices = useCallback(async () => {
        try {
            const res = await serviceAPI.getRecentServices();
            const services = res.data.data || [];
            setRecentServices(services);
            // Apply the selected service's data (default to first = current)
            if (services.length > 0) {
                const svc = services[0];
                serviceIdRef.current = svc.id || null;
                applyServiceData(svc);
                setLastSyncTime(new Date());
                setSyncError(false);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
            setError(`Failed to load services: ${msg}`);
        }
    }, [applyServiceData]);

    // Initial load: once members are ready, fetch recent services
    useEffect(() => {
        if (members.length > 0 && recentServices.length === 0) {
            loadRecentServices();
        }
    }, [members, recentServices.length, loadRecentServices]);

    // Re-apply service data when leaders load
    useEffect(() => {
        if (leaders.length > 0 && selectedService) {
            applyServiceData(selectedService);
        }
    }, [leaders, selectedService, applyServiceData]);

    // SSE: stream attendance updates in real time (only for current service)
    useEffect(() => {
        if (!selectedService || !isCurrentService) return;
        const id = serviceIdRef.current;
        if (!id) return;

        const url = serviceAPI.streamUrl(id);
        const es = new EventSource(url);
        esRef.current = es;

        es.onmessage = (event) => {
            try {
                const svc = JSON.parse(event.data);
                applyServiceData(svc);
                setLastSyncTime(new Date());
                setSyncError(false);
            } catch (err) {
                console.error('SSE parse error:', err);
            }
        };

        es.onerror = () => {
            setSyncError(true);
        };

        return () => {
            es.close();
            esRef.current = null;
        };
    }, [selectedService, isCurrentService, applyServiceData]);

    // Switch between services
    const selectService = (index) => {
        setSelectedIndex(index);
        const svc = recentServices[index];
        if (svc) {
            serviceIdRef.current = svc.id || null;
            applyServiceData(svc);
            setLastSyncTime(svc.is_current ? new Date() : null);
            setSyncError(false);
        }
    };

    // Save an absence reason for a member
    const saveReason = async (memberId, reason) => {
        const id = serviceIdRef.current;
        if (!id) return;
        // Optimistic update
        setReasonMap((prev) => {
            const next = { ...prev };
            if (reason) next[memberId] = reason;
            else delete next[memberId];
            return next;
        });
        try {
            await serviceAPI.updateAbsenceReason(id, { member_id: memberId, reason });
        } catch (err) {
            console.error('saveReason failed:', err.response?.status, err.response?.data || err.message);
        }
    };

    // Toggle a single member's attendance
    const toggleMember = async (memberId) => {
        if (!isCurrentService) return;
        const id = serviceIdRef.current;
        if (!id) return;

        const wasPresent = presentSet.has(memberId);
        const nowPresent = !wasPresent;

        // Optimistic update
        setPresentSet((prev) => {
            const next = new Set(prev);
            if (nowPresent) next.add(memberId);
            else next.delete(memberId);
            return next;
        });
        if (nowPresent) {
            setReasonMap((prev) => { const next = { ...prev }; delete next[memberId]; return next; });
        }

        try {
            await serviceAPI.toggleAttendance(id, {
                member_id: memberId,
                present: nowPresent,
            });
            setLastSyncTime(new Date());
            setSyncError(false);
        } catch (err) {
            console.error('toggleMember failed:', err.response?.status, err.response?.data || err.message);
            setPresentSet((prev) => {
                const next = new Set(prev);
                if (wasPresent) next.add(memberId);
                else next.delete(memberId);
                return next;
            });
            setError('Failed to update attendance');
        }
    };

    const batchToggle = async (memberList, present) => {
        const id = serviceIdRef.current;
        if (!id) return;
        const BATCH_SIZE = 5;
        let failed = 0;
        for (let i = 0; i < memberList.length; i += BATCH_SIZE) {
            const batch = memberList.slice(i, i + BATCH_SIZE);
            const results = await Promise.allSettled(
                batch.map((m) =>
                    serviceAPI.toggleAttendance(id, { member_id: m.id, present })
                )
            );
            const rejected = results.filter((r) => r.status === 'rejected');
            rejected.forEach((r) => console.error('batchToggle rejected:', r.reason?.response?.status, r.reason?.response?.data || r.reason?.message));
            failed += rejected.length;
        }
        if (failed > 0) {
            setError(`${failed} update(s) failed`);
        } else {
            setLastSyncTime(new Date());
        }
    };

    const displayList = attendanceMode === 'leaders' ? leaders : members;

    const setAllPresent = async () => {
        if (!isCurrentService || !serviceIdRef.current) return;
        const absentItems = displayList.filter((m) => !presentSet.has(m.id));
        setPresentSet((prev) => {
            const next = new Set(prev);
            displayList.forEach((m) => next.add(m.id));
            return next;
        });
        await batchToggle(absentItems, true);
    };

    const setAllAbsent = async () => {
        if (!isCurrentService || !serviceIdRef.current) return;
        const presentItems = displayList.filter((m) => presentSet.has(m.id));
        setPresentSet((prev) => {
            const next = new Set(prev);
            displayList.forEach((m) => next.delete(m.id));
            return next;
        });
        await batchToggle(presentItems, false);
    };

    const presentCount = displayList.filter((p) => presentSet.has(p.id)).length;
    const totalCount = displayList.length;

    // Compute sync status text
    let syncText = '';
    if (lastSyncTime) {
        const secsAgo = Math.round((Date.now() - lastSyncTime.getTime()) / 1000);
        syncText = secsAgo < 5 ? 'Synced just now' : `Synced ${secsAgo}s ago`;
    }

    if (!isAuthenticated) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <Card>
                    <CardBody>
                        <h2>Not authenticated</h2>
                        <p>Please login to take attendance.</p>
                        <Button onClick={() => navigate('/login')}>Login</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Attendance</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Mark absent {attendanceMode === 'leaders' ? 'leaders' : 'members'} for a service</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

                <Card>
                    <CardHeader>
                        <div>
                            {selectedService && (
                                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2 flex-wrap">
                                    {formatServiceDate(selectedService.date, selectedService.type)}
                                    <span className={
                                        selectedService.type === 'sunday'
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full text-xs font-medium capitalize'
                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full text-xs font-medium capitalize'
                                    }>
                                        {selectedService.type}
                                    </span>
                                </h2>
                            )}
                            {/* Service picker tabs */}
                            {recentServices.length > 1 && (
                                <div className="flex gap-1.5 mt-2">
                                    {recentServices.map((svc, i) => (
                                        <button
                                            key={svc.date + svc.type}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                                i === selectedIndex
                                                    ? 'bg-accent-500 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                            }`}
                                            onClick={() => selectService(i)}
                                        >
                                            {formatShortDate(svc.date, svc.type)}
                                            {svc.is_current && (
                                                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 align-middle" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {isCurrentService && lastSyncTime && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                    <span className={`w-2 h-2 rounded-full ${syncError ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                    <span>{syncError ? 'Sync error' : syncText}</span>
                                </div>
                            )}
                            {!isCurrentService && selectedService && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                                    <span>Past service — attendance locked, absence reasons editable</span>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardBody>
                        {isAdmin && (
                            <div className="flex gap-1 mb-3">
                                {['members', 'leaders'].map((mode) => (
                                    <button
                                        key={mode}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                            attendanceMode === mode
                                                ? 'bg-accent-500 text-white'
                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                        }`}
                                        onClick={() => setAttendanceMode(mode)}
                                    >
                                        {mode === 'members' ? 'Members' : 'Leaders'}
                                    </button>
                                ))}
                            </div>
                        )}
                        {loading ? (
                            <p className="text-center text-slate-500 py-8">Loading...</p>
                        ) : !selectedService ? (
                            <p className="text-center text-slate-500 py-8">Loading service...</p>
                        ) : displayList.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">No {attendanceMode === 'leaders' ? 'leaders' : 'members'} found</p>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{attendanceMode === 'leaders' ? 'Leaders' : 'Members'} ({presentCount}/{totalCount} present)</h3>
                                    {isCurrentService && (
                                        <div className="flex gap-2">
                                            <button className="text-xs text-accent-500 hover:text-accent-600 font-medium" onClick={setAllPresent}>All Present</button>
                                            <button className="text-xs text-accent-500 hover:text-accent-600 font-medium" onClick={setAllAbsent}>All Absent</button>
                                        </div>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                                    {displayList.map((m) => {
                                        const isAbsent = !presentSet.has(m.id);
                                        const popoverOpen = openPopoverId === m.id;
                                        return (
                                            <div
                                                key={m.id}
                                                className={`card-base p-3 text-center transition-all select-none relative${
                                                    isAbsent ? ' !bg-red-50 !border-red-200 dark:!bg-red-950/30 dark:!border-red-800' : ''
                                                }${
                                                    isCurrentService ? ' cursor-pointer hover:shadow-card-hover' : ' opacity-90'
                                                }${
                                                    popoverOpen ? ' z-30' : ''
                                                }`}
                                                onClick={() => toggleMember(m.id)}
                                            >
                                                <Avatar name={m.name} photoUrl={m.photo_url} size="lg" className={isAbsent ? 'opacity-60' : ''} />
                                                <div className="text-xs text-slate-700 dark:text-slate-300 mt-1.5">{m.name || '\u2014'}</div>
                                                {isAbsent && (
                                                    <AbsenceReasonPopover
                                                        reason={reasonMap[m.id] || ''}
                                                        onSave={(reason) => saveReason(m.id, reason)}
                                                        onOpenChange={(open) => setOpenPopoverId(open ? m.id : null)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

            </main>
        </div>
    );
}

export default Attendance;
